import { useState, useCallback, useEffect } from 'react';
import { deliveryHistoryDetailApi } from '../api/deliveryHistoryDetailApi';
import { DeliverySupplier, DeliveryRider, DeliveryHistoryDetailFilters } from '../types/deliveryHistoryDetail.types';

interface UseDetailOptionsProps {
    isSupplierUser: boolean;
    userId: string;
    filters: DeliveryHistoryDetailFilters;
    setFilters: React.Dispatch<React.SetStateAction<DeliveryHistoryDetailFilters>>;
}

export const useDeliveryDetailOptions = ({ isSupplierUser, userId, filters, setFilters }: UseDetailOptionsProps) => {
    const [suppliers, setSuppliers] = useState<DeliverySupplier[]>([]);
    const [suppliersLoading, setSuppliersLoading] = useState(false);
    const [riders, setRiders] = useState<DeliveryRider[]>([]);
    const [ridersLoading, setRidersLoading] = useState(false);

    const fetchSuppliers = useCallback(async () => {
        setSuppliersLoading(true);
        try {
            const list = await deliveryHistoryDetailApi.getSuppliers();
            setSuppliers(list);

            if (isSupplierUser) {
                const matched = list.find((item) => item.suppId === userId);
                setFilters((prev) => {
                    if (prev.suppId && prev.suppId !== userId) return prev;
                    return { ...prev, suppId: userId, suppName: matched?.suppName || prev.suppName || '' };
                });
            } else {
                setFilters((prev) => {
                    if (prev.suppId || list.length === 0) return prev;
                    return { ...prev, suppId: list[0].suppId, suppName: list[0].suppName || '' };
                });
            }
        } catch {
            setSuppliers([]);
        } finally {
            setSuppliersLoading(false);
        }
    }, [isSupplierUser, userId, setFilters]);

    const fetchRiders = useCallback(
        async (suppId: string) => {
            if (!suppId) {
                setRiders([]);
                return;
            }
            setRidersLoading(true);
            try {
                const list = await deliveryHistoryDetailApi.getRiders(suppId);
                setRiders(list);
                setFilters((prev) => {
                    if (prev.suppId !== suppId) return prev;
                    if (!prev.riderId) return { ...prev, riderName: '' };
                    const matched = list.find((item) => item.riderId === prev.riderId);
                    return matched ? { ...prev, riderName: matched.riderName } : { ...prev, riderId: '', riderName: '' };
                });
            } catch {
                setRiders([]);
                setFilters((prev) => ({ ...prev, riderId: '', riderName: '' }));
            } finally {
                setRidersLoading(false);
            }
        },
        [setFilters]
    );

    useEffect(() => {
        fetchSuppliers();
    }, [fetchSuppliers]);

    useEffect(() => {
        fetchRiders(filters.suppId);
    }, [fetchRiders, filters.suppId]);

    return { suppliers, suppliersLoading, riders, ridersLoading };
};
